import { useEffect, useState } from 'react';
import styled from 'styled-components';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

import {
  getAddonCurrentPrice,
  getAddonPrice,
  getProductCurrentPrice,
  getProductPrice,
  getPriceFromSellingPlanAllocations,
  getFirstAvailableVariant,
  isProductAvailable,
} from '../../utils';
import { formatProductTitle } from '../../utils/BundleUtils';

import MultiSelectDropdown, {
  useMultiSelectDropdown,
} from '../MultiSelectDropdown';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { merge } from 'lodash';

type Props = {
  discount?: {
    type: string;
    value: number;
  };
  sellingPlan?: string | number;
  products: GlobalProduct[];
  onCardClick: (product) => void;
  addProduct: (productId: GlobalVariant) => void;
  removeProduct: (productId: GlobalVariant) => void;
  selectedProducts: Record<string, number>;
  setSelectedProducts: (selectedProducts: Record<string, number>) => void;
  initialSelectedProducts?: Record<string, number>;
  canAddProducts?: boolean;
  baBProductsToCartFlag?: boolean;
  isAddon?: boolean;
  shopConfiguration?: ShopConfiguration;
  hideBaBSoldOutOptions: boolean;
  ignoreBaBOutOfStockAllowance: boolean;
  isClassicBab: boolean;
  isThemed?: boolean;
  ThemedCardContainer?: React.ComponentType<any>;
  ProductCard: React.ComponentType<any>;
  _awtId?: string;
};

export const MultiVariantProductCard = ({
  _awtId,
  discount,
  sellingPlan,
  products,
  onCardClick,
  addProduct,
  removeProduct,
  selectedProducts,
  setSelectedProducts,
  initialSelectedProducts,
  canAddProducts = true,
  baBProductsToCartFlag = false,
  isAddon,
  shopConfiguration,
  hideBaBSoldOutOptions,
  ignoreBaBOutOfStockAllowance,
  isClassicBab,
  isThemed = false,
  ThemedCardContainer,
  ProductCard,
}: Props): JSX.Element => {
  const [selectedVariants, setSelectedVariants] = useState<
    Record<string, string>
  >(initializeSelectedVariants(products, initialSelectedProducts));
  const [cardsProducts, setCardsProducts] = useState<GlobalProduct[]>([]);

  useDeepCompareEffect(() => {
    setCardsProducts(
      setInitialSelectedProductsOptions(
        products,
        initialSelectedProducts,
        selectedProducts,
      ),
    );
  }, [initialSelectedProducts, products]);

  const updateSelectedVariants = (
    productId: string | number,
    variantId: string,
    options: MultiSelectOption[],
  ) => {
    const updatedVariants = selectVariant(
      productId,
      variantId,
      selectedVariants,
    );

    setSelectedVariants(updatedVariants);

    handleUpdateOptions(productId, options);
  };

  const handleUpdateOptions = (
    productId: string | number,
    options: MultiSelectOption[],
  ) => {
    const updatedSelectedOptions = updateSelectedOptions(
      cardsProducts,
      options,
      productId,
    );

    setCardsProducts(updatedSelectedOptions);
  };

  useDeepCompareEffect(() => {
    setSelectedVariants(
      initializeSelectedVariants(products, initialSelectedProducts),
    );
  }, [initialSelectedProducts, products]);

  useEffect(() => {
    const newSelectedProducts = removeOldSelected(
      {
        selectedVariants,
        selectedProducts,
      },
      cardsProducts,
      products,
    );

    setSelectedProducts(newSelectedProducts);
  }, [selectedVariants]);

  return (
    <>
      {cardsProducts.map((product: GlobalProduct) => {
        const selectedVariant = getSelectedVariant(product, selectedVariants);
        let price;
        let currentPrice;

        if (baBProductsToCartFlag && selectedVariant && isAddon) {
          price = getAddonPrice(selectedVariant);
          currentPrice = getAddonCurrentPrice(selectedVariant);
        } else if (baBProductsToCartFlag && selectedVariant) {
          currentPrice = getProductCurrentPrice(selectedVariant);
          price =
            getProductPrice(selectedVariant, discount) ||
            getPriceFromSellingPlanAllocations(selectedVariant, sellingPlan) ||
            currentPrice;
        }

        const updateSelectedVariant = (options) => {
          const variantId = getVariantIdFromOptions(options, product.variants);
          updateSelectedVariants(product.id, variantId, options);
        };

        const areAllVariantsSoldOut = product.variants.every(
          ({ available }) => !available,
        );
        const isAvailable = isProductAvailable(
          selectedVariant,
          shopConfiguration,
          ignoreBaBOutOfStockAllowance,
        );

        if (
          hideBaBSoldOutOptions &&
          ((!product.available && product.hasOnlyDefaultVariant) ||
            areAllVariantsSoldOut)
        ) {
          return null;
        }

        const productCard = (
          <ProductCard
            _awtId={_awtId}
            subtitle={product.metafields?.bundle?.subtitle}
            price={isClassicBab ? undefined : price}
            currentPrice={currentPrice}
            currency={get(window, ['Shopify', 'currency', 'active'], 'USD')}
            key={selectedVariant?.id || 'no-variant'}
            id={selectedVariant?.id || '0'}
            handle={product.handle}
            image={(selectedVariant?.image as string) || product.image}
            quantity={selectedProducts[selectedVariant?.id || 0] || 0}
            title={
              selectedVariant?.name
                ? formatProductTitle(selectedVariant.name)
                : generatePossibleName(product)
            }
            available={isAvailable}
            onIncrease={() =>
              canAddProducts && selectedVariant && addProduct(selectedVariant)
            }
            onDecrease={() => selectedVariant && removeProduct(selectedVariant)}
            onCardClick={(productInfo) =>
              onCardClick({
                ...productInfo,
                images: product.images,
                hasOneVariant: product.hasOnlyDefaultVariant,
                media: product.media,
              })
            }
            description={product.description}
            doNotApplyDiscount={
              baBProductsToCartFlag && selectedVariant && isAddon
            }
            VariantSelector={
              !product.hasOnlyDefaultVariant ? (
                <VariantSelector
                  hasOnlyDefaultVariant={product.hasOnlyDefaultVariant}
                  optionsAndValues={product.optionsAndValues}
                  handleConfirm={updateSelectedVariant}
                  handleUpdateOptions={(options) =>
                    handleUpdateOptions(product.id, options)
                  }
                  available={selectedVariant?.available || false}
                  selected={selectedVariant?.title}
                />
              ) : null
            }
            reviews={product?.reviews}
          />
        );

        return isThemed && ThemedCardContainer ? (
          <ThemedCardContainer
            key={selectedVariant?.id || 'no-variant'}
            _awtId={_awtId}
          >
            {productCard}
          </ThemedCardContainer>
        ) : (
          productCard
        );
      })}
    </>
  );
};

/**
 * This function sets the initial selected products options.
 * It receives the products, the initial selected products and the selected products.
 * It merges the initial selected products and the selected products.
 * It sets the selected variant or the first variant.
 * It sets the selected option or the first option.
 */
function setInitialSelectedProductsOptions(
  products: GlobalProduct[] = [],
  initialSelectedProducts: Record<string, number> = {},
  selectedProducts: Record<string, number> = {},
): GlobalProduct[] {
  // Merge the initial selected products and the selected products
  const mergedSelectedProducts = merge(
    initialSelectedProducts,
    selectedProducts,
  );

  // For each product, find the selected variant or the first variant
  return products.map((product) => {
    const selectedVariant =
      product.variants.find((v) =>
        Object.keys(mergedSelectedProducts).includes(v.id.toString()),
      ) ||
      getFirstAvailableVariant(product.variants) ||
      product.variants[0];

    // For each option, see if it is selected
    const newOptionsAndValues = product.optionsAndValues?.map((option) => {
      // If the option is not found in the selected variant, select the first option
      const selectedOption =
        selectedVariant.options.find((o) => o === option.id) ||
        selectedVariant.options[0];

      return {
        ...option,
        isSelected: selectedOption === option.id,
      };
    });

    return {
      ...product,
      optionsAndValues: newOptionsAndValues,
      variantSelected: selectedVariant,
    };
  });
}

function initializeSelectedVariants(
  products: GlobalProduct[],
  initialSelectedProducts = {},
): Record<string, string> {
  return products.reduce(
    (acc, product) => ({
      ...acc,
      [product.id]: getSelectedVariantId(product, initialSelectedProducts),
    }),
    {} as Record<string, string>,
  );
}

function getSelectedVariantId(
  product: GlobalProduct,
  initialSelectedProducts,
): string {
  const variant = product.variants.find((v) =>
    Object.keys(initialSelectedProducts).includes(v.id.toString()),
  );

  if (!variant)
    return `${
      getFirstAvailableVariant(product.variants)?.id || product.variants[0].id
    }`;

  return `${variant.id}`;
}
function getSelectedVariant(
  product: GlobalProduct,
  selectedVariants: Record<string, string>,
): GlobalVariant | undefined {
  if (product.hasOnlyDefaultVariant) {
    return product.variants[0];
  }

  return product.variants.find(
    (v) => v.id.toString() === `${selectedVariants[product.id]}`,
  );
}

function selectVariant(
  productId: string | number,
  variantId: string,
  selectedVariants: Record<string, string>,
): Record<string, string> {
  const updatedSelectedVariants = {
    ...selectedVariants,
    [productId]: variantId,
  };

  return updatedSelectedVariants;
}

function getVariantIdFromOptions(options, variants) {
  const selectedOptions = options
    .filter(({ isSelected }) => isSelected)
    .map(({ id }) => id);
  const variant = variants.find((v) =>
    isEqual(v.options.toSorted(), selectedOptions.toSorted()),
  );

  return variant?.id;
}

function isProductInSection(
  cardsProducts: GlobalProduct[],
  productId: string,
): boolean {
  return (
    cardsProducts
      .flatMap(({ variants }) => variants)
      .map(({ id }) => String(id))
      .find((e) => e === productId) !== undefined
  );
}

function removeOldSelected(
  { selectedVariants, selectedProducts },
  cardsProducts: GlobalProduct[],
  products: GlobalProduct[],
): Record<string, number> {
  return Object.entries(selectedProducts).reduce(
    (accum, [productId, quantity]) => {
      if (
        !isProductInSection(cardsProducts, productId) ||
        (Object.values(selectedVariants).map(String).includes(productId) &&
          !newVariantChangeInProduct({
            selectedVariants,
            productId,
            cardsProducts,
            products,
          }))
      )
        return {
          ...accum,
          [productId]: quantity,
        };

      if (
        newVariantChangeInProduct({
          selectedVariants,
          productId,
          cardsProducts,
          products,
        })
      ) {
        return {
          ...accum,
          [newVariantChangeInProduct({
            selectedVariants,
            productId,
            cardsProducts,
            products,
          })]: quantity,
        };
      }

      return accum;
    },
    {},
  );
}

const VariantSelector = ({
  hasOnlyDefaultVariant,
  optionsAndValues,
  handleConfirm,
  handleUpdateOptions,
  available,
  selected,
}) => {
  const { isOpen, setIsOpen, handleOnOptionClicked, options } =
    useMultiSelectDropdown({
      initialOptions: optionsAndValues,
      isSingleSelect: false,
    });

  const selectedVariant = () => {
    !hasOnlyDefaultVariant ? setIsOpen(!isOpen) : null;
  };

  const handleClose = () => {
    if (isOpen) handleConfirm(options);
    setIsOpen(false);
  };

  const handleOptionSelected = (option) => {
    const newOptions = handleOnOptionClicked(option);
    handleUpdateOptions(newOptions);
  };

  return (
    <MultiSelectDropdownContainer>
      <MultiSelectDropdown
        isOpen={isOpen}
        options={options}
        onOptionClicked={handleOptionSelected}
        buttonText={'Save'}
        onSelectClick={selectedVariant}
        selectedOption={!hasOnlyDefaultVariant ? selected : ''}
        onClose={handleClose}
        error={!available}
        onConfirm={handleClose}
        withPortal={false}
      />
    </MultiSelectDropdownContainer>
  );
};

const MultiSelectDropdownContainer = styled.div.attrs({
  className: 'awtomic-select-dropdown',
})`
  margin: 1rem 0;
`;

const updateSelectedOptions = (
  cardsProducts: GlobalProduct[],
  options: MultiSelectOption[],
  productId,
): GlobalProduct[] => {
  return cardsProducts.map((product: GlobalProduct) => {
    if (product.id !== productId) return product;

    return {
      ...product,
      optionsAndValues: options,
    };
  });
};

const generatePossibleName = (product: GlobalProduct): JSX.Element => {
  const selectedOptions =
    product.optionsAndValues
      ?.filter(({ isSelected }) => isSelected)
      .map(({ label }) => label) || [];

  return (
    <>
      <span className="awt-product-title">{product.title}</span>
      {selectedOptions.length > 0 && (
        <>
          <span className="awt-dash"> - </span>
          <span className="awt-variant-title">
            {selectedOptions.join(' / ')}
          </span>
        </>
      )}
    </>
  );
};

const newVariantChangeInProduct = ({
  selectedVariants,
  productId,
  cardsProducts,
  products = [],
}): string => {
  const variantsSelectedInDropdownPerProduct = Object.entries(selectedVariants);
  const variantsInEachProduct = cardsProducts.map(({ id, variants }) => [
    String(id),
    variants.map(({ id }) => String(id)),
  ]);
  const productChange = variantsInEachProduct.find(([pId, variants]) =>
    variantsSelectedInDropdownPerProduct.find(
      ([id, _]) => pId === id && variants.includes(productId),
    ),
  );

  const newVariant = variantsSelectedInDropdownPerProduct.find(
    ([id, _]) => id === productChange[0],
  )[1] as string;

  if (
    products
      .flatMap(({ variants }) => variants)
      ?.find(({ id }) => id == newVariant)?.available
  ) {
    return newVariant;
  }

  return;
};
